// Module form definitions: slug → { name, description, Icon, fields[] }
// `description` is outcome-led, peer-to-peer clinical copy
// (BUILD 8 — CamelionAI LLC value-proposition rebuild, Feb 2026).

import {
  Anchor, Layers, Square, Smile, Activity, Gauge, Users,
  FlaskConical, Mail, FileSignature, DollarSign,
} from "lucide-react";

export const MODULE_DEFS = {
  "implant-case-planner": {
    name: "Implant Case Planner",
    description:
      "Anchor every implant case on prosthetically-driven planning — site, bone, biotype, and antagonist load resolved before the surgical guide is designed.",
    Icon: Anchor,
    fields: [
      { name: "site", label: "Implant Site (FDI/Universal)", type: "text", placeholder: "e.g. #19 / 36", required: true },
      { name: "bone_quality", label: "Bone Quality", type: "select", options: ["D1", "D2", "D3", "D4"], required: true },
      { name: "ridge_dimensions", label: "Ridge Dimensions (W x H mm)", type: "text", placeholder: "e.g. 7 x 12 mm" },
      { name: "soft_tissue", label: "Soft Tissue Biotype", type: "select", options: ["Thin", "Medium", "Thick"] },
      { name: "antagonist", label: "Opposing Dentition", type: "select", options: ["Natural Tooth", "Implant Crown", "Removable Prosthesis", "None"] },
      { name: "esthetic_zone", label: "Esthetic Zone?", type: "select", options: ["Yes", "No"] },
      { name: "medical_history", label: "Relevant Medical History", type: "textarea", placeholder: "Diabetes, smoking, bisphosphonates, etc." },
      { name: "additional_notes", label: "Additional Clinical Notes", type: "textarea" },
    ],
  },
  "crown-bridge-designer": {
    name: "Crown and Bridge Designer",
    description:
      "Confirm prep geometry, occlusal load, and material choice before the lab cuts a die — fewer remakes, predictable seats, defensible chart notes.",
    Icon: Layers,
    fields: [
      { name: "prosthesis_type", label: "Prosthesis Type", type: "select", options: ["Single Crown", "3-unit Bridge", "4-unit Bridge", "Cantilever", "Maryland Bridge"], required: true },
      { name: "tooth_numbers", label: "Tooth Number(s)", type: "text", placeholder: "e.g. #8 or #19-21", required: true },
      { name: "abutment_vitality", label: "Abutment Vitality", type: "select", options: ["Vital", "Endodontically Treated", "Mixed"] },
      { name: "esthetic_priority", label: "Esthetic Priority", type: "select", options: ["High (anterior)", "Medium (premolar)", "Low (molar)"] },
      { name: "occlusal_load", label: "Occlusal Load", type: "select", options: ["Light", "Normal", "Heavy / Parafunction"] },
      { name: "preferred_material", label: "Preferred Material (optional)", type: "select", options: ["No preference", "Zirconia", "Lithium Disilicate", "PFM", "Full Gold"] },
      { name: "preparation_status", label: "Preparation Status", type: "select", options: ["Not yet prepared", "Prepared - need verification"] },
      { name: "additional_notes", label: "Additional Clinical Notes", type: "textarea" },
    ],
  },
  "inlay-onlay-advisor": {
    name: "Inlay Onlay Advisor",
    description:
      "Decide direct, inlay, onlay, or full-coverage in seconds — cusp-by-cusp reasoning, remaining-structure analysis, and a defensible note for the chart.",
    Icon: Square,
    fields: [
      { name: "tooth_number", label: "Tooth Number", type: "text", required: true },
      { name: "cavity_classification", label: "Cavity Classification", type: "select", options: ["Class I", "Class II (MO/DO)", "Class II (MOD)", "Class V", "Cusp Replacement"], required: true },
      { name: "cusps_involved", label: "Number of Cusps Involved", type: "select", options: ["0", "1", "2", "3", "4"] },
      { name: "remaining_tooth_structure", label: "Remaining Tooth Structure", type: "select", options: ["Adequate", "Moderately Compromised", "Severely Compromised"] },
      { name: "endodontically_treated", label: "Endodontically Treated?", type: "select", options: ["Yes", "No"] },
      { name: "occlusal_function", label: "Occlusion / Parafunction", type: "select", options: ["Normal", "Bruxism", "Crossbite", "Heavy Wear"] },
      { name: "patient_factors", label: "Patient Factors", type: "textarea", placeholder: "Caries risk, age, budget, esthetic demands" },
    ],
  },
  "smile-design-planner": {
    name: "Smile Design Planner",
    description:
      "Sequence esthetic cases from incisal display down — midline, smile line, shade, and modality resolved before a single tooth is prepared.",
    Icon: Smile,
    fields: [
      { name: "chief_complaint", label: "Patient Chief Complaint", type: "textarea", required: true },
      { name: "teeth_to_treat", label: "Teeth to Treat", type: "text", placeholder: "e.g. #6-11" },
      { name: "smile_line", label: "Smile Line", type: "select", options: ["Low", "Medium", "High", "Gummy"] },
      { name: "midline_status", label: "Midline", type: "select", options: ["Coincident", "Shifted left", "Shifted right", "Canted"] },
      { name: "current_shade", label: "Current Shade", type: "text", placeholder: "e.g. A3" },
      { name: "desired_shade", label: "Desired Shade", type: "text", placeholder: "e.g. BL2" },
      { name: "ortho_history", label: "Orthodontic History", type: "select", options: ["None", "Previous ortho", "Currently in ortho", "Refused ortho"] },
      { name: "preferred_modality", label: "Preferred Modality", type: "select", options: ["No preference", "Veneers", "Crowns", "Composite Bonding", "Bleaching only"] },
      { name: "additional_notes", label: "Additional Notes", type: "textarea" },
    ],
  },
  "all-on-x-planner": {
    name: "Full Arch All-on-X Planner",
    description:
      "Plan immediate-load full-arch rehabs with arch-specific bone, sinus, smoking, and budget logic — every contraindication surfaced before the case books.",
    Icon: Activity,
    fields: [
      { name: "arch", label: "Arch", type: "select", options: ["Maxillary", "Mandibular", "Both"], required: true },
      { name: "current_status", label: "Current Dental Status", type: "select", options: ["Fully edentulous", "Failing dentition", "Existing denture wearer"] },
      { name: "bone_status_maxilla", label: "Maxillary Bone Status", type: "select", options: ["Adequate", "Moderate atrophy", "Severe atrophy", "Sinus pneumatization", "N/A"] },
      { name: "bone_status_mandible", label: "Mandibular Bone Status", type: "select", options: ["Adequate", "Moderate atrophy", "Severe atrophy", "N/A"] },
      { name: "immediate_load_desired", label: "Immediate Load Desired?", type: "select", options: ["Yes", "No"] },
      { name: "smoking_status", label: "Smoking Status", type: "select", options: ["Non-smoker", "<10/day", ">10/day"] },
      { name: "patient_expectations", label: "Patient Expectations", type: "textarea" },
      { name: "budget_tier", label: "Budget Tier", type: "select", options: ["Premium", "Standard", "Economy"] },
    ],
  },
  "occlusion-analyzer": {
    name: "Occlusion Analyzer",
    description:
      "Read the bite before you alter it — Angle class, CR-MI slide, parafunction, and TMD flags translated into an occlusal scheme you can chart.",
    Icon: Gauge,
    fields: [
      { name: "angle_class", label: "Angle Classification", type: "select", options: ["Class I", "Class II Div 1", "Class II Div 2", "Class III"], required: true },
      { name: "canine_guidance", label: "Canine Guidance", type: "select", options: ["Present bilateral", "Present unilateral", "Group function", "Absent"] },
      { name: "overbite", label: "Overbite (mm)", type: "text" },
      { name: "overjet", label: "Overjet (mm)", type: "text" },
      { name: "cr_mi_slide", label: "CR-MI Slide", type: "select", options: ["None", "<1mm", "1-2mm", ">2mm"] },
      { name: "tmd_signs", label: "TMD Signs / Symptoms", type: "textarea", placeholder: "Clicking, deviation, pain, limited opening" },
      { name: "parafunction", label: "Parafunction", type: "select", options: ["None", "Clenching", "Bruxism", "Both"] },
      { name: "wear_pattern", label: "Wear Pattern", type: "textarea" },
    ],
  },
  "denture-rpd-planner": {
    name: "Denture and RPD Planner",
    description:
      "Map every Kennedy class to a defensible removable design — clasp choice, major connector, ridge form, and prognosis captured in a single note.",
    Icon: Users,
    fields: [
      { name: "prosthesis_type", label: "Prosthesis Type", type: "select", options: ["Complete Maxillary", "Complete Mandibular", "Both CD", "RPD Maxillary", "RPD Mandibular"], required: true },
      { name: "kennedy_class", label: "Kennedy Class (if RPD)", type: "select", options: ["N/A", "Class I", "Class II", "Class III", "Class IV"] },
      { name: "remaining_teeth", label: "Remaining Teeth (if any)", type: "text" },
      { name: "ridge_form", label: "Ridge Form", type: "select", options: ["Well-formed", "Moderately resorbed", "Severely resorbed", "Knife-edge", "Flabby"] },
      { name: "previous_prosthesis", label: "Previous Prosthesis Experience", type: "select", options: ["First-time wearer", "Experienced wearer", "Failed previous"] },
      { name: "esthetic_demand", label: "Esthetic Demand", type: "select", options: ["Low", "Medium", "High"] },
      { name: "budget", label: "Budget Considerations", type: "select", options: ["Standard", "Premium", "Cast metal RPD", "Flexible/acrylic"] },
      { name: "additional_notes", label: "Additional Notes", type: "textarea" },
    ],
  },
  "material-science-advisor": {
    name: "Material Science Advisor",
    description:
      "Pick the right ceramic, polymer, or metal the first time — indication-matched with thickness, substrate, and bond environment all factored in.",
    Icon: FlaskConical,
    fields: [
      { name: "indication", label: "Clinical Indication", type: "textarea", placeholder: "e.g. Anterior crown #9, screw-retained implant crown #30", required: true },
      { name: "esthetic_demand", label: "Esthetic Demand", type: "select", options: ["Low", "Medium", "High", "Maximum"] },
      { name: "occlusal_load", label: "Occlusal Load", type: "select", options: ["Light", "Normal", "Heavy", "Parafunction"] },
      { name: "available_thickness", label: "Available Thickness (mm)", type: "text", placeholder: "e.g. 1.5" },
      { name: "substrate", label: "Substrate", type: "select", options: ["Natural tooth - vital", "Natural tooth - endo", "Implant abutment - titanium", "Implant abutment - zirconia"] },
      { name: "cementation_environment", label: "Cementation Environment", type: "select", options: ["Supragingival", "Equigingival", "Subgingival", "Difficult isolation"] },
      { name: "allergies", label: "Patient Allergies / Sensitivities", type: "text" },
    ],
  },
  "referral-letter-generator": {
    name: "Referral Letter Generator",
    description:
      "Send specialist-ready referrals in under a minute — chief complaint, findings, and the specific ask written colleague-to-colleague.",
    Icon: Mail,
    fields: [
      { name: "specialist_type", label: "Specialist Type", type: "select", options: ["Endodontist", "Periodontist", "Oral Surgeon", "Orthodontist", "Prosthodontist", "Pedodontist"], required: true },
      { name: "specialist_name", label: "Specialist Name", type: "text", placeholder: "Dr. Jane Doe" },
      { name: "patient_name", label: "Patient Name", type: "text", required: true },
      { name: "patient_age", label: "Patient Age", type: "text" },
      { name: "chief_complaint", label: "Chief Complaint", type: "textarea" },
      { name: "clinical_findings", label: "Clinical Findings", type: "textarea", required: true },
      { name: "specific_request", label: "Specific Request / Procedure", type: "textarea", required: true },
      { name: "referring_dentist", label: "Referring Dentist Name", type: "text" },
      { name: "referring_practice", label: "Referring Practice Name", type: "text" },
    ],
  },
  "patient-consent-generator": {
    name: "Patient Consent Generator",
    description:
      "Procedure-specific consents that hold up in chart audits — risks, alternatives, anesthesia tier, and special-risk language captured exactly once.",
    Icon: FileSignature,
    fields: [
      { name: "procedure", label: "Procedure", type: "text", placeholder: "e.g. Single implant placement #30 with bone graft", required: true },
      { name: "patient_name", label: "Patient Name", type: "text" },
      { name: "tooth_or_area", label: "Tooth / Area", type: "text" },
      { name: "anesthesia_type", label: "Anesthesia / Sedation", type: "select", options: ["Local only", "Local + Nitrous", "Oral sedation", "IV sedation", "General"] },
      { name: "special_risks", label: "Special Risks to Emphasize", type: "textarea", placeholder: "e.g. Mental nerve proximity, sinus elevation" },
      { name: "alternatives", label: "Alternatives Available", type: "textarea" },
      { name: "estimated_cost", label: "Estimated Cost (optional)", type: "text" },
    ],
  },
  "case-fee-estimator": {
    name: "Case Fee Estimator",
    description:
      "Price treatment plans with regional UCR data and lab-tier logic — present fees with confidence and stop leaving margin on the table.",
    Icon: DollarSign,
    fields: [
      { name: "procedures", label: "Procedures (one per line)", type: "textarea", placeholder: "Single implant #30\nCustom abutment\nScrew-retained zirconia crown\nCBCT", required: true },
      { name: "region", label: "US Region", type: "select", options: ["Northeast", "Southeast", "Midwest", "Southwest", "West Coast", "Mountain West"] },
      { name: "practice_tier", label: "Practice Tier", type: "select", options: ["Premium / Boutique", "Standard private practice", "Value / Insurance-driven"] },
      { name: "lab_tier", label: "Lab Tier Used", type: "select", options: ["Premium domestic", "Mid-range domestic", "Offshore / Value"] },
      { name: "insurance_involved", label: "Insurance Involved?", type: "select", options: ["No - fee for service", "PPO in-network", "PPO out-of-network", "HMO"] },
    ],
  },
};

export const MODULE_SLUGS = Object.keys(MODULE_DEFS);
